import { useState, useEffect } from 'preact/hooks';
import type { RecipeCategory, Cuisine } from '../types';

const CATEGORY_EMOJI: Record<RecipeCategory, string> = {
  sandwich: '🥪',
  pinwheel: '🌯',
  quesadilla: '🫓',
  bento: '🍱',
  thermos: '🍝',
  pasta: '🍝',
  'rice-grain': '🍚',
  'mini-muffin': '🧁',
  'breakfast-for-lunch': '🥞',
  dippable: '🥕',
  'hidden-veggie': '🥦',
};

const CATEGORY_GRAD: Record<RecipeCategory, string> = {
  sandwich: 'var(--grad-sandwich)',
  pinwheel: 'var(--grad-pinwheel)',
  quesadilla: 'var(--grad-quesadilla)',
  bento: 'var(--grad-bento)',
  thermos: 'var(--grad-thermos)',
  pasta: 'var(--grad-pasta)',
  'rice-grain': 'var(--grad-rice-grain)',
  'mini-muffin': 'var(--grad-mini-muffin)',
  'breakfast-for-lunch': 'var(--grad-breakfast-for-lunch)',
  dippable: 'var(--grad-dippable)',
  'hidden-veggie': 'var(--grad-hidden-veggie)',
};

const CUISINE_EMOJI: Record<Cuisine, string> = {
  vietnamese: '🍜',
  chinese: '🥟',
  korean: '🍲',
  japanese: '🍣',
  thai: '🥘',
  american: '🍔',
  other: '🍽️',
};

const CUISINE_GRAD: Record<Cuisine, string> = {
  vietnamese: 'var(--grad-vietnamese)',
  chinese: 'var(--grad-chinese)',
  korean: 'var(--grad-korean)',
  japanese: 'var(--grad-japanese)',
  thai: 'var(--grad-thai)',
  american: 'var(--grad-american)',
  other: 'var(--grad-other)',
};

export function EmojiBadge(props: {
  category: RecipeCategory | null;
  cuisine: Cuisine | null;
  imageUrl: string | null;
  imageFocus?: string | null;
  height?: number;
}) {
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setErrored(false);
  }, [props.imageUrl]);

  const h = props.height ?? 130;
  const emoji = props.category
    ? CATEGORY_EMOJI[props.category]
    : (props.cuisine ? CUISINE_EMOJI[props.cuisine] : '🍽️');
  const grad = props.category
    ? CATEGORY_GRAD[props.category]
    : (props.cuisine ? CUISINE_GRAD[props.cuisine] : 'var(--grad-other)');
  const label = props.category ?? props.cuisine ?? 'recipe';

  const showImage = !!props.imageUrl && !errored;
  const showEmoji = !showImage;

  return (
    <div
      role="img"
      aria-label={label}
      style={{
        height: h,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: grad,
        fontSize: 56,
        lineHeight: 1,
        borderTopLeftRadius: 'var(--radius-card)',
        borderTopRightRadius: 'var(--radius-card)',
      }}
    >
      {props.imageUrl && (
        <img
          src={props.imageUrl}
          alt=""
          draggable={false}
          onError={() => setErrored(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: props.imageFocus ?? '50% 65%',
            display: showImage ? 'block' : 'none',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        />
      )}
      {showEmoji && emoji}
    </div>
  );
}
